import React, { useEffect, useState, useRef } from "react";
import Invoice from "../invoice/Invoice";
import { useReactToPrint } from "react-to-print";
import Modal from "../../utilities/components/Modal";
import KeycloakService from "../config/KeycloakService";
import {
  getInvoicesByUserId,
  hydrateInvoiceForView,
} from "../invoice/InvoiceServices";
import { InvoiceModel } from "../../utilities/models/InvoiceModel";
import { EntitiesListResponseModel } from "../../utilities/models/EntitiesListResponseModel";
import { useLoadingSpinner } from "../../utilities/components/LoadingSpinnerProvider";
import InvoiceCard from "./management/InvoiceCard";
import { getUserById } from "../user/userService";
import CustomToast from "../../utilities/components/CustomToast";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Printer,
} from "lucide-react";

const InvoicesManagement = () => {
  const setIsLoading = useLoadingSpinner();
  const [params, setParams] = useState({ size: 9, page: 0 });
  const [invoicesData, setInvoicesData] = useState(EntitiesListResponseModel);
  const [selectedInvoice, setSelectedInvoice] = useState(InvoiceModel);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Invoice-${selectedInvoice.invoiceNumber || "Document"}`,
  });

  const fetchInvoices = () => {
    setIsLoading(true);
    getInvoicesByUserId(
      KeycloakService.getUserId(),
      params,
      setInvoicesData,
      setIsLoading,
    );
  };

  useEffect(() => {
    fetchInvoices();
  }, [params]);

  const handleViewInvoice = async (invoice) => {
    setIsLoading(true);

    try {
      const hydratedInvoice = await hydrateInvoiceForView({
        invoice,
        userId: KeycloakService.getUserId(),
        getUserById,
      });

      if (!hydratedInvoice?.reservation) {
        CustomToast(
          "We couldn't load the reservation details for this invoice right now.",
          "error",
        );
        return;
      }

      setSelectedInvoice(hydratedInvoice);
      setIsViewModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < invoicesData.totalPages) {
      setParams((prev) => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-serif tracking-wide">
            Invoices
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            View and manage your payment history.
          </p>
        </div>
      </div>

      {/* Content Grid */}
      {invoicesData.content && invoicesData.content.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {invoicesData.content.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              onView={handleViewInvoice}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 border-dashed">
          <div className="mx-auto w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4 text-slate-400">
            <FileText size={32} />
          </div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">
            No invoices found
          </h3>
          <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto mt-1">
            You don't have any invoices generated yet.
          </p>
        </div>
      )}

      {/* Pagination Actions */}
      {invoicesData.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-800 pt-4">
          <button
            onClick={() => handlePageChange(params.page - 1)}
            disabled={params.page === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Page{" "}
            <span className="font-medium text-slate-900 dark:text-white">
              {params.page + 1}
            </span>{" "}
            of <span className="font-medium">{invoicesData.totalPages}</span>
          </span>
          <button
            onClick={() => handlePageChange(params.page + 1)}
            disabled={params.page >= invoicesData.totalPages - 1}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={setIsViewModalOpen}
        maxModalWidth={"sm:max-w-4xl"}
      >
        <div className="flex flex-col space-y-4 mt-6">
          <div className="flex flex-row space-x-2 px-4 sm:px-6">
            {/* ----- Print Button ----- */}
            <button
              onClick={handlePrint}
              className="flex flex-row items-center bg-blue-600 hover:bg-blue-500 space-x-2 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-all shadow-sm"
            >
              <Printer size={20} />
              <span>Print / Save PDF</span>
            </button>
          </div>

          <div className="px-4 sm:px-6 max-h-[70vh] overflow-y-auto">
            {/* Wrap Invoice in a div for viewing */}
            <div className="p-4 bg-white text-slate-900">
              <Invoice invoice={selectedInvoice} />
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800 px-4 py-3 sm:flex sm:flex-row sm:justify-end sm:px-6 rounded-b-lg border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-slate-700 px-3 py-2 text-sm font-semibold text-slate-900 dark:text-white shadow-sm ring-1 ring-inset ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 sm:mt-0 sm:w-auto transition-colors"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Hidden Print Container - Always rendered to ensure ref availability */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <div ref={componentRef}>
          <Invoice invoice={selectedInvoice} />
        </div>
      </div>
    </div>
  );
};

export default InvoicesManagement;
